/**
 * Component gallery — the Phase 0 checkpoint page. Plan §9 Phase 0 asks for a
 * "Storybook-like /client/src/dev/gallery", and addendum §I.6 makes
 * accessibility a Phase 0 requirement verified per component: every item below
 * is keyboard reachable and shows the focus ring from tokens.css.
 *
 * Compare against `/design/mockups/01-global-header/*.png` and `nav-*.png` in
 * both light and dark.
 */
import { useState } from 'react'
import { DEFAULT_BRAND_COLOR, type MeResponse } from '@rowted/shared'
import { AppFrame } from '../frame/app-frame.js'
import { PageBar } from '../frame/page-bar.js'
import {
  Avatar, Button, Card, Checkbox, Chip, EmptyState, KVGrid, MetricCard, ProgressBar,
  SectionLabel, Segmented, StatusChip, Stepper, Switch, Tag,
} from '../components/primitives.js'
import { Modal, PinPad, Popover, SlideOver, Timeline, Toast } from '../components/overlays.js'
import {
  AddressFields, CheckRow, ChipsField, ComboInput, DateInput, Field, FormGrid, FormSection,
  MoneyInput, NumberInput, PairField, PhotoDrop, Readout, ReadonlyInput, SegmentedField,
  SelectInput, SwatchField, SwitchRow, TextArea, TextInput, Wide,
  type AddressValue, type ComboItem,
} from '../components/form/index.js'
import { Band } from '../components/primitives.js'
import { CellStack, DataList, EMPTY_CELL } from '../components/data-list.js'

/** A stand-in `me` so the gallery renders without a session. */
export const GALLERY_ME: MeResponse = {
  // The gallery never calls the API, so this is a placeholder, not a session.
  csrfToken: 'gallery',
  user: {
    id: '00000000-0000-0000-0000-000000000001',
    name: 'Alex Kline',
    firstName: 'Alex',
    lastName: 'Kline',
    title: 'Account manager',
    phone: '717-555-0148',
    email: 'alex@keystonegrounds.com',
    initials: 'AK',
    mfaEnrolled: true,
    hasPin: false,
    mfaRequired: true,
    pinExpected: false,
    passwordChangedAt: '2026-06-01T00:00:00.000Z',
    recoveryCodesLeft: 6,
    timeZone: 'America/New_York',
    notificationPrefs: { matrix: {}, quietHours: { from: '21:00', to: '06:00' } },
  },
  tenant: {
    id: '00000000-0000-0000-0000-0000000000ff',
    name: 'Keystone Grounds',
    logoUrl: null,
    logoDarkUrl: null,
    // The shared constant, not a hex: the gallery is a page of components,
    // and a colour typed here would be the one hand-picked one in the client.
    brandColor: DEFAULT_BRAND_COLOR,
    currency: 'USD',
  },
  roles: [{ id: '00000000-0000-0000-0000-00000000000a', name: 'Owner' }],
  grants: [],
  defaultApp: 'office',
  permissions: {
    contacts: 'full', properties: 'full', jobs: 'full', quotes: 'full', visits: 'full',
    schedule: 'full', purchasing: 'full', inventory: 'full', equipment: 'full',
    invoices: 'full', payments: 'full', costs_and_margins: 'full', employees: 'full',
    roles_and_permissions: 'full', company_settings: 'full', audit_log: 'full',
    reporting: 'full', portal_admin: 'full',
  },
  scope: {
    available: [
      { id: 'u-company', kind: 'company', name: 'Keystone Grounds', code: 'KG', parentId: null, branchCount: 4, districtCount: 0, yardName: null, crewCount: 0, closed: false, reachable: true },
      { id: 'u-central', kind: 'region', name: 'Central PA', code: 'CPA', parentId: 'u-company', branchCount: 2, districtCount: 1, yardName: null, crewCount: 0, closed: false, reachable: true },
      { id: 'u-harrisburg', kind: 'district', name: 'Harrisburg district', code: 'HBD', parentId: 'u-central', branchCount: 2, districtCount: 0, yardName: null, crewCount: 0, closed: false, reachable: true },
      { id: 'u-hbg-branch', kind: 'branch', name: 'Harrisburg', code: 'HBG', parentId: 'u-harrisburg', branchCount: 1, districtCount: 0, yardName: 'Harrisburg yard', crewCount: 6, closed: false, reachable: true },
      { id: 'u-camphill', kind: 'branch', name: 'Camp Hill', code: 'CHL', parentId: 'u-harrisburg', branchCount: 1, districtCount: 0, yardName: 'Camp Hill yard', crewCount: 3, closed: false, reachable: true },
      { id: 'u-pittsburgh', kind: 'region', name: 'Pittsburgh', code: 'PGH', parentId: 'u-company', branchCount: 0, districtCount: 0, yardName: null, crewCount: 0, closed: false, reachable: false },
    ],
    roots: ['u-harrisburg'],
    selected: ['u-hbg-branch', 'u-camphill'],
    label: 'Harrisburg district',
    labelKind: 'district',
  },
  impersonation: null,
  features: [],
  demo: null,
}

interface DemoJob {
  id: string
  ref: string
  customer: string
  property: string
  status: string
  expected: number
  hours: number
}

const DEMO_JOBS: DemoJob[] = [
  { id: '1', ref: 'J-2260', customer: 'Meridian Property Group', property: 'Riverfront Office Park', status: 'scheduled', expected: 18940, hours: 24 },
  { id: '2', ref: 'J-2291', customer: 'Meridian Property Group', property: 'Meridian Plaza', status: 'quoted', expected: 20080, hours: 31.5 },
  { id: '3', ref: 'J-2304', customer: 'Harrisburg Public Library', property: 'Camp Hill Library', status: 'completed', expected: 4320, hours: 12 },
]

const money = (value: number) => `$${value.toLocaleString('en-US')}`

/** What the field kit's combo searches, so the create row can be tried. */
const GALLERY_COMPANIES: ComboItem[] = [
  { id: '1', name: 'Keystone Builders', sub: 'Harrisburg · 3 properties · bill-to' },
  { id: '2', name: 'Keys & Sons Paving', sub: 'York' },
  { id: '3', name: 'Meridian Property Group', sub: 'Harrisburg · 11 properties' },
]

export function Gallery() {
  const [selectedMetric, setSelectedMetric] = useState<string | null>('completed')
  const [switchOn, setSwitchOn] = useState(true)
  const [density, setDensity] = useState<'default' | 'compact'>('default')
  const [quantity, setQuantity] = useState(3)
  const [pin, setPin] = useState('')
  const [slideOver, setSlideOver] = useState(false)
  const [modal, setModal] = useState(false)
  const [popover, setPopover] = useState(false)
  /* The field kit's own state, so the gallery's controls actually work. */
  const [company, setCompany] = useState<ComboItem | null>(null)
  const [priced, setPriced] = useState<'company' | 'own' | 'fixed'>('company')
  const [when, setWhen] = useState('2026-09-12')
  const [divisions, setDivisions] = useState<string[]>(['maint', 'enh'])
  const [swatch, setSwatch] = useState<string | null>('amber')
  const [address, setAddress] = useState<AddressValue>({
    line1: '41 Grantley Rd', line2: '', city: 'York', state: 'PA', postalCode: '17403',
  })

  return (
    <AppFrame
      me={GALLERY_ME}
      active="jobs"
      unreadCount={4}
      // Today-relevant counts only, never vanity totals (inventory, nav states).
      badges={{
        visits: { text: '12 today' },
        purchasing: { text: '3' },
        invoices: { text: '5 overdue', tone: 'danger' },
      }}
    >
      <PageBar
        title="Component gallery"
        sub="Phase 0 · every component in plan §3.6"
        tabs={[
          { key: 'all', label: 'All components' },
          { key: 'frame', label: 'Frame' },
          { key: 'lists', label: 'Lists' },
        ]}
        activeTab="all"
        actions={
          <>
            <Button>Export view</Button>
            <Button>Import</Button>
            <Button variant="primary">New job</Button>
          </>
        }
        onSaveView={() => {}}
      />

      <div className="flex flex-col gap-6 p-4">
        <Section title="Metric cards" note="Each one is a saved filter and is selectable (plan §3.5).">
          <div className="flex flex-wrap gap-2">
            <MetricCard label="Requested" value="14" sub="3 today" dotColor="rgb(var(--status-job-requested-fg))"
              selected={selectedMetric === 'requested'} onClick={() => setSelectedMetric('requested')} />
            <MetricCard label="Quoted" value="38" sub="$412,900" dotColor="rgb(var(--status-job-quoted-fg))"
              selected={selectedMetric === 'quoted'} onClick={() => setSelectedMetric('quoted')} />
            <MetricCard label="Completed · not invoiced" value="12" sub="$48,120" dotColor="rgb(var(--status-job-completed-fg))"
              selected={selectedMetric === 'completed'} onClick={() => setSelectedMetric('completed')} />
            <MetricCard label="Invoiced · unpaid" value="5" sub="2 overdue" dotColor="rgb(var(--status-invoice-overdue-fg))"
              selected={selectedMetric === 'unpaid'} onClick={() => setSelectedMetric('unpaid')} />
          </div>
        </Section>

        <Section title="Buttons, chips, tags">
          <div className="flex flex-wrap items-center gap-2">
            <Button>Secondary</Button>
            <Button variant="primary">Primary</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="danger">Void invoice</Button>
            <Button size="big" variant="primary">Touch target (44px)</Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Chip active label="Season">2026</Chip>
            <Chip label="Status">Scheduled</Chip>
            <Chip>Mine</Chip>
            <Tag>Draft v2</Tag>
            <Tag>admin</Tag>
          </div>
        </Section>

        <Section title="Status chips" note="The semantic pairs in plan §3.1 — never a hand-typed colour.">
          <div className="flex flex-wrap items-center gap-2">
            {['requested', 'quoted', 'approved', 'scheduled', 'in_progress', 'completed', 'invoiced', 'paid'].map((status) => (
              <StatusChip key={status} group="job" status={status} />
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {['draft', 'awaiting_approval', 'sent', 'partially_paid', 'paid', 'overdue'].map((status) => (
              <StatusChip key={status} group="invoice" status={status} />
            ))}
          </div>
        </Section>

        <Section title="The list engine" note="One DataList for every module (plan §3.5).">
          <div className="mb-2 flex items-center gap-2">
            <Segmented
              label="Density"
              value={density}
              onChange={setDensity}
              options={[{ value: 'default', label: 'Default' }, { value: 'compact', label: 'Compact' }]}
            />
          </div>
          <DataList
            density={density}
            rowKey={(job) => job.id}
            mark={{
              render: (job: { status: string }) => (
                <span
                  aria-hidden
                  className="h-full w-[4px]"
                  style={{ background: `rgb(var(--status-job-${job.status}-fg))` }}
                />
              ),
            }}
            columns={[
              {
                key: 'job', header: 'Job', width: 220,
                render: (job) => <CellStack primary={job.property} secondary={`${job.ref} · ${job.customer}`} />,
              },
              {
                key: 'status', header: 'Status', width: 150,
                render: (job) => <StatusChip group="job" status={job.status} />,
              },
              {
                key: 'expected', header: 'Expected', numeric: true, width: 130,
                render: (job) => money(job.expected),
                aggregate: ({ rows }) => money(rows.reduce((sum, job) => sum + job.expected, 0)),
              },
              {
                key: 'hours', header: 'Hours', numeric: true, width: 100,
                render: (job) => `${job.hours} h`,
                aggregate: ({ rows }) => `${rows.reduce((sum, job) => sum + job.hours, 0)} h`,
              },
              {
                key: 'margin', header: 'Margin', numeric: true, width: 110,
                // Hidden entirely for roles with Costs & margins = None (§6.3).
                render: () => EMPTY_CELL,
              },
            ]}
            groups={[
              { key: 'scheduled', label: 'Scheduled', rows: DEMO_JOBS.filter((j) => j.status === 'scheduled') },
              { key: 'quoted', label: 'Quoted', rows: DEMO_JOBS.filter((j) => j.status === 'quoted') },
              { key: 'completed', label: 'Completed', rows: DEMO_JOBS.filter((j) => j.status === 'completed') },
            ]}
          />
        </Section>

        <Section title="Form controls">
          <div className="flex flex-wrap items-center gap-6">
            <Switch checked={switchOn} onChange={setSwitchOn} label="Auto-send invoices" />
            <Checkbox label="Bill-to contact" defaultChecked />
            <Checkbox label="Some properties" indeterminate />
            <Checkbox label="Unchecked" />
            <Stepper value={quantity} onChange={setQuantity} label="bags of mulch" />
            <Stepper value={quantity} onChange={setQuantity} label="bags of mulch, touch" touch />
          </div>
          <div className="flex items-center gap-3">
            <Avatar name="Alex Kline" self />
            <Avatar name="Sam Okafor" />
            <Avatar name="Rita Park" size={24} />
          </div>
        </Section>

        {/*
          * Every part of the field kit, in every state —
          * `24-record-flyouts/flyout-field-kit.png` is what this section is
          * checked against. A control that is not here is a control a form
          * should not be drawing.
          */}
        <Section
          title="Forms · the field kit"
          note="record-flyouts.md §3. Control 36px, radius 9, label 11.5 sentence case, required = a green dot. Compare with 24-record-flyouts/flyout-field-kit.png."
        >
          <div className="grid gap-5 lg:grid-cols-2">
            <Card className="flex flex-col gap-3 p-3.5">
              <FormSection title="Text · the five states" />
              <Field label="Name" required>
                {({ id }) => <TextInput id={id} placeholder="Hardwood mulch, bagged" />}
              </Field>
              <Field label="Name" required>
                {({ id }) => <TextInput id={id} defaultValue="Hardwood mulch · double-shredded" />}
              </Field>
              <Field label="Name" required error="A material needs a name">
                {({ id, describedBy, invalid }) => (
                  <TextInput id={id} describedBy={describedBy} invalid={invalid} defaultValue="" />
                )}
              </Field>
              <Field label="Code" hint="set when the item is created">
                {({ id, describedBy }) => (
                  <TextInput id={id} describedBy={describedBy} value="MUL-DS" disabled readOnly />
                )}
              </Field>
              <Field label="Last paid">
                {({ id, describedBy }) => (
                  <ReadonlyInput
                    id={id}
                    describedBy={describedBy}
                    value="$22.40 · Aug 30 · Lancaster Farms"
                  />
                )}
              </Field>
              <Field label="Tagline" optional>
                {({ id }) => <TextInput id={id} defaultValue="Commercial grounds since 1998" />}
              </Field>
              <Field label="Email" hint="proposals and invoices go here">
                {({ id, describedBy }) => (
                  <TextInput
                    id={id}
                    describedBy={describedBy}
                    defaultValue="york@keystonegrounds.com"
                    suffix="verified"
                  />
                )}
              </Field>
            </Card>

            <Card className="flex flex-col gap-3 p-3.5">
              <FormSection title="Numbers, money, units" sub="tabular, right-aligned, no spinner" />
              <FormGrid>
                <Field label="Cost per yd">
                  {({ id }) => <MoneyInput id={id} defaultValue="24.00" />}
                </Field>
                <Field label="Minimum order">
                  {({ id }) => <NumberInput id={id} unit="yd" defaultValue="5" />}
                </Field>
                <Field label="Markup on cost">
                  {({ id }) => <NumberInput id={id} percent defaultValue="35" />}
                </Field>
                <Field label="Hours per unit">
                  {({ id }) => <NumberInput id={id} unit="h" defaultValue="0.5" />}
                </Field>
                <Wide>
                  <Field label="Quantity" hint="the 44px size is for the one number a popup is about">
                    {({ id, describedBy }) => (
                      <NumberInput
                        id={id}
                        describedBy={describedBy}
                        size="big"
                        unit="sq ft"
                        defaultValue="12"
                      />
                    )}
                  </Field>
                </Wide>
              </FormGrid>

              <FormSection title="Compound pairs" sub="two fields, one sentence" />
              <PairField
                joiner="at"
                left={(
                  <Field label="Bought by">
                    {({ id, describedBy }) => (
                      <SelectInput
                        id={id}
                        describedBy={describedBy}
                        label="Bought by"
                        value="yd"
                        onChange={() => undefined}
                        options={[{ value: 'yd', label: 'Yard (yd)' }]}
                      />
                    )}
                  </Field>
                )}
                right={<Field label="Cost per yd">{({ id }) => <MoneyInput id={id} defaultValue="24.00" />}</Field>}
              />
            </Card>

            <Card className="flex flex-col gap-3 p-3.5">
              <FormSection title="Select, combo, create-inline" sub="never a native select" />
              <Field label="Usual vendor" hint="vendors arrive with the first purchase order">
                {({ id, describedBy }) => (
                  <SelectInput
                    id={id}
                    describedBy={describedBy}
                    label="Usual vendor"
                    value={null}
                    onChange={() => undefined}
                    placeholder="None yet"
                    options={[{ value: 'lancaster', label: 'Lancaster Farms Supply' }]}
                  />
                )}
              </Field>
              <Field label="Company" hint="type to search · create what is not there">
                {({ id, describedBy }) => (
                  <ComboInput
                    id={id}
                    describedBy={describedBy}
                    label="Company"
                    thing="company"
                    placeholder="Search companies…"
                    value={company}
                    onPick={setCompany}
                    search={(query) => GALLERY_COMPANIES.filter((one) =>
                      one.name.toLowerCase().includes(query.trim().toLowerCase()))}
                    onCreate={async (name) => ({ id: name, name, sub: 'new' })}
                  />
                )}
              </Field>
              <Field group label="Priced by">
                {({ labelId, describedBy }) => (
                  <SegmentedField
                    labelledBy={labelId}
                    describedBy={describedBy}
                    label="Priced by"
                    value={priced}
                    onChange={setPriced}
                    options={[
                      { value: 'company', label: 'Company markup · 35%' },
                      { value: 'own', label: 'Its own markup' },
                      { value: 'fixed', label: 'A fixed price' },
                    ]}
                  />
                )}
              </Field>
              <FormGrid>
                <Field label="Purchased on">
                  {({ id }) => (
                    <DateInput id={id} label="Purchased on" value={when} onChange={setWhen} />
                  )}
                </Field>
                <Field label="Plate expires">
                  {({ id }) => (
                    <DateInput
                      id={id}
                      label="Plate expires"
                      value=""
                      onChange={() => undefined}
                    />
                  )}
                </Field>
              </FormGrid>
            </Card>

            <Card className="flex flex-col gap-3 p-3.5">
              <FormSection title="Switch rows, check rows, chips" />
              <div>
                <SwitchRow
                  label="Track stock at the yards"
                  sub="on-hand, reserved and purchasing follow this item"
                  checked={switchOn}
                  onChange={setSwitchOn}
                />
                <SwitchRow
                  label="Rentable"
                  sub="monthly rate, term and deposit — needs the Rentals feature"
                  checked={false}
                  onChange={() => undefined}
                  disabled
                />
              </div>
              <div>
                <CheckRow label="Taxable" checked onChange={() => undefined} />
                <CheckRow
                  label="Show on proposal"
                  sub="customers see the line"
                  checked
                  onChange={() => undefined}
                />
                <CheckRow
                  label="Optional"
                  sub="The customer decides whether to add it"
                  checked={false}
                  onChange={() => undefined}
                />
              </div>
              <Field label="Divisions">
                {() => (
                  <ChipsField
                    label="Divisions"
                    options={[
                      { value: 'maint', label: 'Maintenance' },
                      { value: 'enh', label: 'Enhancements' },
                      { value: 'irr', label: 'Irrigation' },
                      { value: 'hard', label: 'Hardscape' },
                    ]}
                    value={divisions}
                    onChange={setDivisions}
                    add={{ label: 'Limit to a branch', onClick: () => undefined }}
                  />
                )}
              </Field>
              <Field label="Colour">
                {() => (
                  <SwatchField
                    label="Colour"
                    value={swatch}
                    onChange={setSwatch}
                    options={[
                      { value: 'green', css: 'rgb(var(--color-accent))', label: 'Green' },
                      { value: 'amber', css: 'rgb(var(--status-quoteLine-deployment-fg))', label: 'Amber' },
                      { value: 'blue', css: 'rgb(var(--status-quoteLine-item-fg))', label: 'Blue' },
                      { value: 'teal', css: 'rgb(var(--status-quoteLine-labor-fg))', label: 'Teal' },
                    ]}
                  />
                )}
              </Field>
              <Field label="Photo">
                {() => (
                  <PhotoDrop
                    onPick={() => undefined}
                    sub="JPG, PNG or HEIC · used on the catalog and the proposal"
                  />
                )}
              </Field>
            </Card>

            <Card className="flex flex-col gap-3 p-3.5">
              <FormSection title="Text area, address" />
              <Field label="Notes">
                {({ id }) => (
                  <TextArea id={id} defaultValue="Gate code 4471 · dog in the yard · park on Grantley" />
                )}
              </Field>
              <AddressFields value={address} onChange={setAddress} />
            </Card>

            <Card className="flex flex-col gap-3 p-3.5">
              <FormSection title="The form talks back" sub="a band or a readout, never a paragraph" />
              <Band tone="green" title="1 yd costs $24.00 and allocates as 1 yd — a job is costed at $24.00 per yd.">
                A skid of brick that covers 800 sq ft is “skid”, “sf”, 800.
              </Band>
              <Band tone="amber" title="Recent purchases averaged $22.40 — 7% under this cost.">
                Use $22.40
              </Band>
              <Readout
                items={[
                  { label: 'Cost / yd', value: '$24.00' },
                  { label: 'Sells / yd', value: '$32.40' },
                  { label: 'Margin', value: '26%', tone: 'accent' },
                ]}
              />
              <Readout
                items={[
                  { label: 'Cost / yd', value: '$24.00' },
                  { label: 'Sells / yd', value: '$26.00' },
                  { label: 'Margin', value: '8%', tone: 'danger' },
                ]}
              />
            </Card>
          </div>
        </Section>

        <Section title="Cards and readouts">
          <div className="grid gap-3 md:grid-cols-2">
            <Card className="p-3">
              <SectionLabel className="mb-2 block">Site &amp; access</SectionLabel>
              <KVGrid
                rows={[
                  { key: 'Address', value: '1200 Front St, Harrisburg' },
                  { key: 'Gate code', value: '#4417' },
                  { key: 'Measured area', value: <span className="tnum">18,400 sq ft</span> },
                  { key: 'Zone', value: 'Route N-3' },
                ]}
              />
            </Card>
            <Card className="p-3">
              <SectionLabel className="mb-2 block">Thursday capacity</SectionLabel>
              <div className="flex flex-col gap-2">
                <ProgressBar value={6.5} max={8} label="Okafor crew hours" />
                <span className="text-sub text-text-muted">6.5 of 8 h booked</span>
                <ProgressBar value={9.5} max={8} label="Baker crew hours" />
                <span className="text-sub text-warn">9.5 of 8 h — over capacity</span>
              </div>
            </Card>
          </div>
        </Section>

        <Section title="History timeline" note="Rendered from audit_log on every record (plan §4.2).">
          <Card className="p-3">
            <Timeline
              entries={[
                { id: '1', at: '4m ago', who: 'Jordan Alvarez', what: 'Approved quote Q-2291', detail: 'Option A + seasonal color · $20,080' },
                { id: '2', at: '2h ago', who: 'Alex Kline', what: 'Changed status', detail: 'Quoted → Approved' },
                { id: '3', at: 'Sep 12', who: 'Sam Okafor', what: 'Changed password', detail: 'Security event — no field values recorded' },
              ]}
            />
          </Card>
        </Section>

        <Section title="Overlays">
          <div className="relative flex flex-wrap items-center gap-2">
            <Button onClick={() => setSlideOver(true)}>Open slide-over</Button>
            <Button onClick={() => setModal(true)}>Open modal</Button>
            <span className="relative">
              <Button onClick={() => setPopover((v) => !v)}>Open popover</Button>
              <Popover open={popover} onClose={() => setPopover(false)} label="Example popover" width={260}>
                <div className="p-3 text-cell text-text-muted">
                  Popovers are the only place the app uses a shadow, besides slide-overs (plan §3.3).
                </div>
              </Popover>
            </span>
          </div>
          <div className="flex max-w-[520px] flex-col gap-2">
            <Toast tone="success">Invoice INV-4402 sent to Rita Park.</Toast>
            <Toast tone="warn">Baker crew is 9.5 h on an 8 h day Thursday.</Toast>
            <Toast tone="danger" onDismiss={() => {}}>That email bounced. Fix the contact and resend.</Toast>
          </div>
        </Section>

        <Section title="Crew PIN pad" note="Clock-in on a registered crew iPad (addendum §G.3).">
          <Card className="w-fit p-6">
            <PinPad value={pin} onChange={setPin} onSubmit={() => setPin('')} />
          </Card>
        </Section>

        <Section title="Empty state">
          <Card>
            <EmptyState
              title="Nothing needs you today"
              body="When a quote is approved or an invoice goes overdue, it shows up here."
              action={{ label: 'Open the jobs board', onClick: () => {} }}
            />
          </Card>
        </Section>
      </div>

      <SlideOver
        open={slideOver}
        onClose={() => setSlideOver(false)}
        title="Portal access"
        footer={<Button variant="primary" onClick={() => setSlideOver(false)}>Send invite</Button>}
      >
        <p className="text-body text-text-muted">
          Slide-overs are 360–440px on the right, with one action in the footer.
        </p>
      </SlideOver>

      <Modal
        open={modal}
        onClose={() => setModal(false)}
        title="Void this invoice?"
        footer={
          <>
            <Button onClick={() => setModal(false)}>Cancel</Button>
            <Button variant="danger" onClick={() => setModal(false)}>Void with reason</Button>
          </>
        }
      >
        Voiding reverses the entry in QuickBooks and needs a reason on the record.
      </Modal>
    </AppFrame>
  )
}

function Section({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <div>
        <h2 className="m-0 text-card text-text">{title}</h2>
        {note && <p className="m-0 text-sub text-text-muted">{note}</p>}
      </div>
      {children}
    </section>
  )
}
